import { getEscalationPolicy } from "@/api/escalationPolicy";
import { fetchTeamMembers, type MemberNameMapResponse } from "@/api/member";
import { ApiError } from "@/shared/libs/axiosClient";
import { useMemberNameMap } from "@/shared/hooks/useMemberNameMap";
import {
  createScheduleOverride,
  createScheduleRotation,
  deleteScheduleOverride,
  downloadCalendar,
  fetchScheduleOverrides,
  fetchScheduleRotation,
  updateScheduleOverride,
  updateScheduleRotation,
  type GapWarning,
  type ScheduleOverrideResponse,
  type ScheduleRotationResponse,
} from "@/api/schedule";
import { useTeamStore } from "@/app/store/useTeamStore";
import { NoTeamSelected } from "@/shared/components/NoTeamSelected";
import { Button } from "@/shared/components/ui/button";
import { Card, CardContent } from "@/shared/components/ui/card";
import { Checkbox } from "@/shared/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { toast } from "@/shared/hooks/useToast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowRight, ArrowUp, Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

// ── 날짜 유틸 ─────────────────────────────────────────────────
function toDateStr(d: Date) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function formatDateKo(iso: string) {
  return new Date(iso).toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

function formatDateTimeKo(iso: string) {
  return new Date(iso).toLocaleString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getMemberName(id: number, members: MemberNameMapResponse[]) {
  return members.find((m) => m.id === id)?.name ?? "알 수 없음";
}

// ── Edit Rotation Modal ───────────────────────────────────────
interface EditRotationModalProps {
  open: boolean;
  onClose: () => void;
  teamId: number;
  rotation: ScheduleRotationResponse | null;
  teamMembers: MemberNameMapResponse[];
  allMembers: MemberNameMapResponse[];
  escalationMemberIds: Set<number>;
  onGapWarning: (warning: GapWarning) => void;
}

function EditRotationModal({
  open,
  onClose,
  teamId,
  rotation,
  teamMembers,
  allMembers,
  escalationMemberIds,
  onGapWarning,
}: EditRotationModalProps) {
  const queryClient = useQueryClient();

  const todayStr = toDateStr(new Date());
  const [startsAt, setStartsAt] = useState(todayStr);
  const [initialStartsAt, setInitialStartsAt] = useState(todayStr);
  const [durationDays, setDurationDays] = useState(7);
  const [orderedIds, setOrderedIds] = useState<number[]>([]);
  const [selectedAddId, setSelectedAddId] = useState<number | "">("");
  const [addWarning, setAddWarning] = useState<string | null>(null);

  // open 시 rotation 데이터로 폼 동기화
  useEffect(() => {
    if (open) {
      const initial = rotation ? rotation.starts_at.slice(0, 10) : todayStr;
      setStartsAt(initial);
      setInitialStartsAt(initial);
      setDurationDays(rotation?.duration_days ?? 7);
      setOrderedIds(rotation ? rotation.members.map((g) => g[0]).filter(Boolean) : []);
      setSelectedAddId("");
      setAddWarning(null);
    }
  }, [open, rotation, todayStr]);

  const moveUp = (i: number) => {
    if (i === 0) return;
    setOrderedIds((prev) => {
      const next = [...prev];
      [next[i - 1], next[i]] = [next[i], next[i - 1]];
      return next;
    });
  };
  const moveDown = (i: number) => {
    if (i === orderedIds.length - 1) return;
    setOrderedIds((prev) => {
      const next = [...prev];
      [next[i], next[i + 1]] = [next[i + 1], next[i]];
      return next;
    });
  };
  const removeMember = (id: number) =>
    setOrderedIds((prev) => prev.filter((v) => v !== id));
  const addMember = () => {
    if (selectedAddId === "" || orderedIds.includes(selectedAddId as number)) return;
    const id = selectedAddId as number;
    // 에스컬레이션 정책에 직접 지정된 멤버는 스케줄에 편성할 수 없음
    // (로테이션 primary + 정책 직접 호출 중복 페이징 방지 — backend `validate_duty_member_ids`와 동일 규칙)
    if (escalationMemberIds.has(id)) {
      setAddWarning(
        `${getMemberName(id, allMembers)} 님은 에스컬레이션 정책에 지정되어 있어 로테이션에 추가할 수 없습니다.\n먼저 정책에서 제외해주세요.`,
      );
      return;
    }
    setAddWarning(null);
    setOrderedIds((prev) => [...prev, id]);
    setSelectedAddId("");
  };

  const saveMut = useMutation({
    mutationFn: () => {
      const memberGroups = orderedIds.map((id) => [id]);
      const payload = {
        starts_at: new Date(startsAt).toISOString(),
        duration_days: durationDays,
        members: memberGroups,
      };
      return rotation
        ? updateScheduleRotation(teamId, payload)
        : createScheduleRotation(teamId, payload);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["schedule-rotation", teamId] });
      queryClient.invalidateQueries({ queryKey: ["duty", teamId] });
      queryClient.invalidateQueries({ queryKey: ["duty-today", teamId] });
      queryClient.invalidateQueries({ queryKey: ["team-members", teamId] });
      onClose();
      if (data.gap_warning) {
        onGapWarning(data.gap_warning);
      }
    },
  });

  // members that can be added (team members not yet in the list)
  const addableMemberIds = teamMembers
    .map((m) => m.id)
    .filter((id) => !orderedIds.includes(id));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>스케줄 로테이션 수정</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Start At */}
          <div className="space-y-1.5">
            <Label htmlFor="rot-starts">시작일</Label>
            <Input
              id="rot-starts"
              type="date"
              value={startsAt}
              min={initialStartsAt < todayStr ? initialStartsAt : todayStr}
              onChange={(e) => setStartsAt(e.target.value)}
            />
            {startsAt < todayStr && startsAt !== initialStartsAt && (
              <p className="text-xs text-destructive">
                과거 날짜로는 변경할 수 없습니다. 오늘 이후 날짜로 설정해주세요.
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              로테이션 수정은 오늘 이후부터 적용됩니다. 과거 이력은 보존됩니다.
            </p>
          </div>

          {/* Duration */}
          <div className="space-y-1.5">
            <Label htmlFor="rot-duration">기간 (일)</Label>
            <Input
              id="rot-duration"
              type="number"
              min={1}
              value={durationDays}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                setDurationDays(Math.max(1, Number.isNaN(v) ? 1 : v));
              }}
            />
          </div>

          {/* Member order list */}
          <div className="space-y-1.5">
            <Label>멤버 (로테이션 순서)</Label>
            <div className="rounded-md border">
              {orderedIds.length === 0 ? (
                <p className="p-3 text-sm text-muted-foreground">멤버를 추가해주세요</p>
              ) : (
                <div className="divide-y">
                  {orderedIds.map((id, i) => (
                    <div key={id} className="flex items-center justify-between px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <span className="w-5 text-sm text-muted-foreground">{i + 1}.</span>
                        <span className="text-sm font-medium">
                          {getMemberName(id, allMembers)}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          aria-label={`${getMemberName(id, allMembers)} 위로 이동`}
                          onClick={() => moveUp(i)}
                          disabled={i === 0}
                        >
                          <ArrowUp className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          aria-label={`${getMemberName(id, allMembers)} 아래로 이동`}
                          onClick={() => moveDown(i)}
                          disabled={i === orderedIds.length - 1}
                        >
                          <ArrowDown className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          className="h-7 text-xs"
                          aria-label={`${getMemberName(id, allMembers)} 제거`}
                          onClick={() => removeMember(id)}
                        >
                          제거
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Add member */}
          <div className="space-y-1.5">
            <Label>멤버 추가</Label>
            <div className="flex gap-2">
              <select
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                value={selectedAddId}
                onChange={(e) => {
                  setSelectedAddId(e.target.value === "" ? "" : parseInt(e.target.value, 10));
                  setAddWarning(null);
                }}
              >
                <option value="">추가할 멤버 선택</option>
                {addableMemberIds.map((id) => (
                  <option key={id} value={id}>
                    {getMemberName(id, allMembers)}
                  </option>
                ))}
              </select>
              <Button
                variant="outline"
                onClick={addMember}
                disabled={selectedAddId === "" || addableMemberIds.length === 0}
              >
                추가
              </Button>
            </div>
            {addWarning && (
              <p className="whitespace-pre-line text-xs text-destructive">{addWarning}</p>
            )}
          </div>

          {saveMut.isError && (
            <p className="text-sm text-destructive">
              {saveMut.error instanceof Error ? saveMut.error.message : "저장에 실패했습니다."}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            취소
          </Button>
          <Button
            onClick={() => saveMut.mutate()}
            disabled={
              saveMut.isPending ||
              orderedIds.length === 0 ||
              (startsAt < todayStr && startsAt !== initialStartsAt)
            }
          >
            {saveMut.isPending ? "저장 중..." : "저장"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Override 테이블 (재사용) ──────────────────────────────────
interface OverrideTableProps {
  rows: ScheduleOverrideResponse[];
  allMembers: MemberNameMapResponse[];
  selectedIds: Set<number>;
  onToggleRow: (id: number) => void;
  onToggleAll: () => void;
  onRowClick: (ov: ScheduleOverrideResponse) => void;
  emptyText: string;
  disableCheckbox?: boolean;
}

function OverrideTable({
  rows, allMembers, selectedIds, onToggleRow, onToggleAll, onRowClick, emptyText, disableCheckbox = false,
}: OverrideTableProps) {
  const allSelected = !disableCheckbox && rows.length > 0 && rows.every((ov) => selectedIds.has(ov.id));
  return (
    <Card>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/40">
              <th className="w-10 px-3 py-2.5 text-left">
                <Checkbox checked={allSelected} onCheckedChange={onToggleAll} disabled={disableCheckbox} />
              </th>
              <th className="px-3 py-2.5 text-left font-medium text-muted-foreground">시작일</th>
              <th className="px-3 py-2.5 text-left font-medium text-muted-foreground">종료일</th>
              <th className="px-3 py-2.5 text-left font-medium text-muted-foreground">멤버</th>
              <th className="px-3 py-2.5 text-left font-medium text-muted-foreground">메모</th>
              <th className="px-3 py-2.5 text-left font-medium text-muted-foreground">수정일</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-8 text-center text-muted-foreground">{emptyText}</td>
              </tr>
            ) : (
              rows.map((ov) => (
                <tr
                  key={ov.id}
                  className="cursor-pointer border-b last:border-b-0 hover:bg-muted/30"
                  tabIndex={0}
                  role="button"
                  aria-label={`오버라이드 ${ov.starts_at} ~ ${ov.ends_at}`}
                  onClick={() => onRowClick(ov)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onRowClick(ov);
                    }
                  }}
                >
                  <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedIds.has(ov.id)}
                      onCheckedChange={() => { if (!disableCheckbox) onToggleRow(ov.id); }}
                      disabled={disableCheckbox}
                    />
                  </td>
                  <td className="px-3 py-2.5">{formatDateKo(ov.starts_at)}</td>
                  <td className="px-3 py-2.5">{formatDateKo(ov.ends_at)}</td>
                  <td className="px-3 py-2.5">
                    {ov.new_duty_member_ids.map((id) => getMemberName(id, allMembers)).join(", ")}
                  </td>
                  <td className="px-3 py-2.5 text-muted-foreground">{ov.note ?? "—"}</td>
                  <td className="px-3 py-2.5 text-muted-foreground">
                    {ov.updated_at
                      ? formatDateTimeKo(ov.updated_at)
                      : ov.created_at
                        ? formatDateTimeKo(ov.created_at)
                        : "—"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ── Add/Edit Override Modal ───────────────────────────────────
interface OverrideModalProps {
  open: boolean;
  onClose: () => void;
  teamId: number;
  teamMembers: MemberNameMapResponse[];
  existing?: ScheduleOverrideResponse; // present → edit mode
  readOnly?: boolean;
}

function OverrideModal({ open, onClose, teamId, teamMembers, existing, readOnly = false }: OverrideModalProps) {
  const queryClient = useQueryClient();
  const isEdit = !!existing;
  const todayStr = toDateStr(new Date());

  const [startsAt, setStartsAt] = useState(todayStr);
  const [endsAt, setEndsAt] = useState(todayStr);
  const [checkedIds, setCheckedIds] = useState<Set<number>>(new Set());
  const [note, setNote] = useState("");

  // existing 또는 open 변경 시 폼 동기화
  useEffect(() => {
    if (open) {
      setStartsAt(existing ? existing.starts_at.slice(0, 10) : todayStr);
      setEndsAt(existing ? existing.ends_at.slice(0, 10) : todayStr);
      setCheckedIds(existing ? new Set(existing.new_duty_member_ids) : new Set());
      setNote(existing?.note ?? "");
    }
  }, [open, existing, todayStr]);

  const toggleMember = (id: number) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const reset = () => {
    setStartsAt(todayStr);
    setEndsAt(todayStr);
    setCheckedIds(new Set());
    setNote("");
  };

  const saveMut = useMutation({
    mutationFn: () => {
      const payload = {
        starts_at: new Date(startsAt).toISOString(),
        ends_at: new Date(endsAt).toISOString(),
        new_duty_member_ids: [...checkedIds],
        ...(note.trim() ? { note: note.trim() } : {}),
      };
      return isEdit
        ? updateScheduleOverride(teamId, existing!.id, payload)
        : createScheduleOverride(teamId, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedule-overrides", teamId] });
      queryClient.invalidateQueries({ queryKey: ["duty", teamId] });
      queryClient.invalidateQueries({ queryKey: ["duty-today", teamId] });
      queryClient.invalidateQueries({ queryKey: ["team-members", teamId] });
      if (!isEdit) reset();
      onClose();
    },
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>스케줄 오버라이드</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Date range */}
          <div className="space-y-1.5">
            <Label htmlFor="ov-starts">시작일</Label>
            <Input
              id="ov-starts"
              type="date"
              value={startsAt}
              min={readOnly ? undefined : todayStr}
              disabled={readOnly}
              onChange={(e) => {
                setStartsAt(e.target.value);
                if (endsAt < e.target.value) setEndsAt(e.target.value);
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ov-ends">종료일</Label>
            <Input
              id="ov-ends"
              type="date"
              value={endsAt}
              min={readOnly ? undefined : startsAt}
              disabled={readOnly}
              onChange={(e) => setEndsAt(e.target.value)}
            />
          </div>

          {/* Member checkboxes */}
          <div className="space-y-1.5">
            <Label>멤버</Label>
            <div className="max-h-48 overflow-y-auto rounded-md border">
              {teamMembers.map((m) => (
                <label
                  key={m.id}
                  htmlFor={`member-${m.id}`}
                  className={`flex items-center gap-3 px-3 py-2.5 ${readOnly ? "cursor-default" : "cursor-pointer hover:bg-muted/50"}`}
                >
                  <Checkbox
                    id={`member-${m.id}`}
                    checked={checkedIds.has(m.id)}
                    disabled={readOnly}
                    onCheckedChange={() => toggleMember(m.id)}
                  />
                  <span className="text-sm font-medium">{m.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Note */}
          <div className="space-y-1.5">
            <Label htmlFor="ov-note">메모</Label>
            <Input
              id="ov-note"
              placeholder="오버라이드 사유"
              value={note}
              disabled={readOnly}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>

          {saveMut.isError && (
            <p className="text-sm text-destructive">
              {saveMut.error instanceof Error ? saveMut.error.message : "저장에 실패했습니다."}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {readOnly ? "닫기" : "취소"}
          </Button>
          {!readOnly && (
            <Button
              onClick={() => saveMut.mutate()}
              disabled={saveMut.isPending || checkedIds.size === 0}
            >
              {saveMut.isPending ? "저장 중..." : "저장"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── 메인 페이지 ───────────────────────────────────────────────
export function OncallSchedulePage() {
  const { selectedTeam } = useTeamStore();
  const [editRotationOpen, setEditRotationOpen] = useState(false);
  const [overrideModalOpen, setOverrideModalOpen] = useState(false);
  const [editingOverride, setEditingOverride] = useState<ScheduleOverrideResponse | undefined>(undefined);
  const [overrideReadOnly, setOverrideReadOnly] = useState(false);
  const [selectedOverrideIds, setSelectedOverrideIds] = useState<Set<number>>(new Set());
  const [showPast, setShowPast] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [calendarDownloading, setCalendarDownloading] = useState(false);
  const [gapWarning, setGapWarning] = useState<GapWarning | null>(null);

  useEffect(() => {
    setSelectedOverrideIds(new Set());
    setEditingOverride(undefined);
    setOverrideModalOpen(false);
    setEditRotationOpen(false);
    setOverrideReadOnly(false);
    setIsDeleteConfirmOpen(false);
    setGapWarning(null);
  }, [selectedTeam?.id]);

  const openAddOverride = () => {
    setEditingOverride(undefined);
    setOverrideReadOnly(false);
    setOverrideModalOpen(true);
  };
  const openEditOverride = (ov: ScheduleOverrideResponse) => {
    setEditingOverride(ov);
    setOverrideReadOnly(false);
    setOverrideModalOpen(true);
  };
  const openViewOverride = (ov: ScheduleOverrideResponse) => {
    setEditingOverride(ov);
    setOverrideReadOnly(true);
    setOverrideModalOpen(true);
  };

  const { data: rotation = null } = useQuery({
    queryKey: ["schedule-rotation", selectedTeam?.id],
    queryFn: () => fetchScheduleRotation(selectedTeam!.id),
    enabled: !!selectedTeam,
    retry: (failureCount, err: unknown) => {
      if (err instanceof Error && err.message.includes("404")) return false;
      return failureCount < 2;
    },
  });

  const { data: overrides = [] } = useQuery({
    queryKey: ["schedule-overrides", selectedTeam?.id],
    queryFn: () => fetchScheduleOverrides(selectedTeam!.id),
    enabled: !!selectedTeam,
  });

  const { nameMap } = useMemberNameMap();
  const allMembers = useMemo(() => Array.from(nameMap.values()), [nameMap]);

  const { data: teamMemberLinks = [] } = useQuery({
    queryKey: ["team-members", selectedTeam?.id],
    queryFn: () => fetchTeamMembers(selectedTeam!.id),
    enabled: !!selectedTeam,
  });

  // 에스컬레이션 정책에 직접 지정된 step2+ member_id 집합 (로테이션 추가 가드용)
  const { data: escalationPolicy = null } = useQuery({
    queryKey: ["escalation-policy", selectedTeam?.id],
    queryFn: async () => {
      try {
        return await getEscalationPolicy(selectedTeam!.id);
      } catch (error) {
        if (error instanceof ApiError && error.statusCode === 404) return null;
        throw error;
      }
    },
    enabled: !!selectedTeam,
  });

  const escalationMemberIds = useMemo(() => {
    const ids = new Set<number>();
    for (const step of escalationPolicy?.steps ?? []) {
      for (const t of step.alert_targets) {
        if (typeof t.member_id === "number") ids.add(t.member_id);
      }
    }
    return ids;
  }, [escalationPolicy]);

  const queryClient = useQueryClient();

  const deleteMut = useMutation({
    mutationFn: async () => {
      const pastIds = new Set(pastOverrides.map((ov) => ov.id));
      const ids = [...selectedOverrideIds].filter((id) => !pastIds.has(id));
      const results = await Promise.allSettled(
        ids.map((id) => deleteScheduleOverride(selectedTeam!.id, id)),
      );
      const succeededIds = ids.filter((_, i) => results[i].status === "fulfilled");
      const failedCount = results.filter((r) => r.status === "rejected").length;
      return { succeededIds, failedCount };
    },
    onSuccess: ({ succeededIds, failedCount }) => {
      queryClient.invalidateQueries({ queryKey: ["schedule-overrides", selectedTeam?.id] });
      queryClient.invalidateQueries({ queryKey: ["duty", selectedTeam?.id] });
      queryClient.invalidateQueries({ queryKey: ["duty-today", selectedTeam?.id] });
      queryClient.invalidateQueries({ queryKey: ["team-members", selectedTeam?.id] });
      setSelectedOverrideIds((prev) => {
        const next = new Set(prev);
        succeededIds.forEach((id) => { next.delete(id); });
        return next;
      });
      if (failedCount > 0) {
        toast({
          variant: "destructive",
          title: "오버라이드 삭제 실패",
          description: `${failedCount}개 오버라이드를 삭제하지 못했습니다.`,
        });
      }
    },
  });

  if (!selectedTeam) return <NoTeamSelected />;

  // 팀 멤버 객체 목록
  const teamMemberIds = new Set(teamMemberLinks.map((tm) => tm.member_id));
  const teamMembers = allMembers.filter((m) => teamMemberIds.has(m.id));

  // rotation member IDs: flatten [[1],[2],[3]] → [1,2,3]
  const rotationMemberIds: number[] = rotation
    ? rotation.members.map((g) => g[0]).filter((id): id is number => id != null)
    : [];

  // override list 분리 — KST 자정 기준 (백엔드 _is_completely_past와 일관)
  const todayKstStr = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Seoul" }).format(new Date());
  const todayKstStart = new Date(`${todayKstStr}T00:00:00+09:00`);
  const upcomingOverrides = overrides
    .filter((ov) => new Date(ov.ends_at) >= todayKstStart)
    .sort((a, b) => a.starts_at.localeCompare(b.starts_at));
  const pastOverrides = overrides
    .filter((ov) => new Date(ov.ends_at) < todayKstStart)
    .sort((a, b) => b.starts_at.localeCompare(a.starts_at));

  const toggleOverrideSelect = (id: number) => {
    setSelectedOverrideIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const makeToggleAll = (rows: typeof overrides) => () => {
    const ids = rows.map((ov) => ov.id);
    const allSelected = ids.every((id) => selectedOverrideIds.has(id));
    setSelectedOverrideIds((prev) => {
      const next = new Set(prev);
      if (allSelected) ids.forEach((id) => { next.delete(id); });
      else ids.forEach((id) => { next.add(id); });
      return next;
    });
  };

  async function handleDownloadCalendar() {
    if (!selectedTeam) return;
    setCalendarDownloading(true);
    try {
      await downloadCalendar(selectedTeam.id);
    } finally {
      setCalendarDownloading(false);
    }
  }

  return (
    <div className="space-y-6 p-6">
      {/* ── Page Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">스케줄 관리</h1>
          <p className="text-sm text-muted-foreground">{selectedTeam.name}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={!selectedTeam || !rotation || calendarDownloading}
          onClick={handleDownloadCalendar}
        >
          {calendarDownloading ? "다운로드 중…" : "캘린더 다운로드 (.ics)"}
        </Button>
      </div>

      {/* ── Schedule Rotation ── */}
      <div>
        <h2 className="mb-3 text-base font-semibold">스케줄 로테이션</h2>
        <Card
          className="cursor-pointer transition-colors hover:bg-muted/30"
          role="button"
          tabIndex={0}
          aria-label="로테이션 편집"
          onClick={() => setEditRotationOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setEditRotationOpen(true);
            }
          }}
        >
          <CardContent className="p-6">
            {rotation ? (
              <div className="space-y-3">
                <div className="flex items-center gap-6 text-sm">
                  <div>
                    <span className="text-muted-foreground">시작일:&nbsp;</span>
                    <span className="font-medium">{formatDateKo(rotation.starts_at)}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">기간:&nbsp;</span>
                    <span className="font-medium">{rotation.duration_days}일</span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-sm text-muted-foreground">멤버:&nbsp;</span>
                  <div className="flex flex-wrap items-center gap-1">
                    {rotationMemberIds.map((id, i) => (
                      <span key={id} className="flex items-center gap-1">
                        <span className="rounded-md border bg-muted px-2.5 py-0.5 text-sm font-medium">
                          {getMemberName(id, allMembers)}
                        </span>
                        {i < rotationMemberIds.length - 1 && (
                          <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  로테이션이 설정되지 않았습니다. 클릭하여 설정하세요.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Schedule Overrides ── */}
      <div className="space-y-4">
        {/* 헤더 + 버튼 */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold">스케줄 오버라이드</h2>
            <p className="text-xs text-muted-foreground">과거 날짜에는 오버라이드를 추가할 수 없습니다.</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={selectedOverrideIds.size === 0 || deleteMut.isPending}
              onClick={() => setIsDeleteConfirmOpen(true)}
            >
              <Trash2 className="mr-1.5 h-3.5 w-3.5" />
              삭제
            </Button>
            <Button size="sm" onClick={openAddOverride}>
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              추가
            </Button>
          </div>
        </div>

        {/* 예정 오버라이드 테이블 */}
        <OverrideTable
          rows={upcomingOverrides}
          allMembers={allMembers}
          selectedIds={selectedOverrideIds}
          onToggleRow={toggleOverrideSelect}
          onToggleAll={makeToggleAll(upcomingOverrides)}
          onRowClick={openEditOverride}
          emptyText="예정된 오버라이드가 없습니다"
        />

        {/* 이전 목록 조회 / 숨기기 버튼 */}
        <div className="flex justify-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowPast((v) => !v)}
          >
            {showPast ? "숨기기" : "이전 목록 조회"}
          </Button>
        </div>

        {/* 이전 오버라이드 테이블 (토글) */}
        {showPast && (
          <OverrideTable
            rows={pastOverrides}
            allMembers={allMembers}
            selectedIds={selectedOverrideIds}
            onToggleRow={toggleOverrideSelect}
            onToggleAll={makeToggleAll(pastOverrides)}
            onRowClick={openViewOverride}
            emptyText="이전 오버라이드가 없습니다"
            disableCheckbox
          />
        )}
      </div>

      {/* ── Modals ── */}
      <EditRotationModal
        open={editRotationOpen}
        onClose={() => setEditRotationOpen(false)}
        teamId={selectedTeam.id}
        rotation={rotation}
        teamMembers={teamMembers}
        allMembers={allMembers}
        escalationMemberIds={escalationMemberIds}
        onGapWarning={setGapWarning}
      />

      {/* Gap Warning Dialog */}
      <Dialog open={gapWarning !== null} onOpenChange={(o) => !o && setGapWarning(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>스케줄 공백 안내</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-foreground">{gapWarning?.message}</p>
          <DialogFooter>
            <Button onClick={() => setGapWarning(null)}>확인</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <OverrideModal
        open={overrideModalOpen}
        onClose={() => setOverrideModalOpen(false)}
        teamId={selectedTeam.id}
        teamMembers={teamMembers}
        existing={editingOverride}
        readOnly={overrideReadOnly}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>오버라이드 삭제</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            선택한 {selectedOverrideIds.size}개 오버라이드를 삭제하시겠습니까?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)}>
              취소
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                deleteMut.mutate();
                setIsDeleteConfirmOpen(false);
              }}
            >
              삭제
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
